<template>
  <ToDoContent
    :class="className"
    class="to-do-add"
  >
    <template #header>
      <div class="to-do-add__heading">
        <BaseHeading
          level="2"
          color="monochrome-06"
        >
          {{ toDo.id ? "Edit ToDo Item" : "Add ToDo Item" }}
        </BaseHeading>
      </div>
    </template>
    <template #content>
      <div class="to-do-add__assignee-row">
        <ToDoSelectUser
          :rules="validators.isRequired('Owner')"
          :toDo="toDo"
          @onSelectedUsers="onSelectedUsers"
          @onSelectedTeams="onSelectedTeams"
        />
        <div class="to-do-add__scope">
          <button
            v-for="scope in recipientScopes"
            :key="scope"
            type="button"
            class="to-do-add__scope-button"
            :class="{ 'is-active': toDo.completionRule === scope }"
            @click="handleInput({ id: 'completionRule', value: scope })"
          >
            {{ $t(`toDoCompletionRule.${scope}`) }}
          </button>
        </div>
      </div>
      <TodoWidgetDatePicker
        :today="today"
        :tomorrow="tomorrow"
        :urgent="toDo.urgent"
        :date="toDo.estimatedDeadline"
        @onCheckboxTomorrow="onCheckboxTomorrow"
        @onCheckboxToday="onCheckboxToday"
        @updateUrgent="updateUrgent"
        @updateDate="updateDate"
      />
      <div class="to-do-add__label-row">
        <span>Label:</span>
        <CustomLabel
          :label="toDo.label"
          searchType="TODO_ITEM"
          @onLabelUpdate="handleInput"
        />
      </div>
      <div
        v-if="isDismissConfirmationVisible"
        class="to-do-add__dismiss-confirmation"
      >
        <BaseHeading
          level="3"
          color="monochrome-06"
        >
          Dismiss ToDo
        </BaseHeading>
        <BaseSubHeading
          size="large"
          weight="400"
          color="monochrome-05"
          darkColor="monochrome-04"
          class="to-do-add__dismiss-description"
        >
          This ToDo will be permanently removed from Sent to Others and won’t appear in your active
          list.
        </BaseSubHeading>
        <div class="to-do-add__dismiss-controls">
          <button
            type="button"
            class="secondary-button btn"
            @click="isDismissConfirmationVisible = false"
          >
            Cancel
          </button>
          <button
            type="button"
            class="primary-button btn to-do-add__dismiss-confirm-button"
            @click="onDismissToDo"
          >
            Dismiss
          </button>
        </div>
      </div>
      <Field
        v-else
        v-model="toDoName"
        as="div"
        :validateOnBlur="false"
        name="toDoDescription"
        class="to-do__description__wrapper"
        :rules="validators.isRequired('Description')"
      >
        <Editor
          id="toDoDescription"
          :value="toDo.structuredDescription.html"
          :extensions="extensions"
          :triggerHandleInputOnMount="false"
          :taskEditor="true"
          class="to-do__description"
          @handleInput="handleInputEditor"
          @input.stop
        />
        <ErrorMessage
          class="error-text error-text-small"
          name="toDoDescription"
        />
      </Field>
    </template>
    <template #footer="{ validate, handleReset }">
      <div
        v-if="!isDismissConfirmationVisible"
        class="to-do-add__footer"
      >
        <div class="to-do-add__footer-actions">
          <button
            type="button"
            class="back-button"
            @click="onCancel"
          >
            Back
          </button>
          <span
            v-if="canDismissToDo"
            class="to-do-add__footer-divider"
            aria-hidden="true"
          />
          <button
            v-if="canDismissToDo"
            type="button"
            class="to-do-add__dismiss-button"
            @click="isDismissConfirmationVisible = true"
          >
            Dismiss
          </button>
        </div>
        <button
          v-if="toDoName.trim()"
          type="button"
          class="primary-button primary-button_small btn"
          :disabled="$wait.is('save.todo')"
          @click="onValidateAndSave(validate, handleReset)"
        >
          Save
        </button>
      </div>
    </template>
  </ToDoContent>
</template>

<script lang="ts">
import { mixins, Options } from "vue-class-component"
import { Prop } from "vue-property-decorator"
import { Action, Getter } from "s-vuex-class"
import { ErrorMessage, Field } from "vee-validate"

import CustomLabel from "@/components/CustomLabel/CustomLabel.vue"
import Editor from "@/components/Editor/index.vue"
import ToDoContent from "@/components/ToDo/ToDoContent.vue"
import ToDoCreateAndUpdate from "@/components/ToDo/ToDoCreateAndUpdate"
import ToDoSelectUser from "@/components/ToDo/ToDoSelectUser.vue"
import TodoWidgetDatePicker from "@/components/ToDo/TodoWidgetDatePicker.vue"

import { FileMetaData, TypeConfig, UploadAttachment, User } from "@/models"
import { ToDoCompletionRule } from "@/models/ToDo"
import { AttachmentFile } from "@/plugins/extensions/Attachment"
import { ReferenceAllUser, ReferenceAt } from "@/plugins/extensions/reference/referenceAt"
import {
  ReferenceHashEpic,
  ReferenceHashFile,
  ReferenceHashIdea,
  ReferenceHashItem,
  ReferenceHashMilestone,
  ReferenceHashProject,
  ReferenceHashSprint,
  ReferenceHashTask,
  ReferenceHashTeam,
  ReferenceHashTip
} from "@/plugins/extensions/reference/referenceHash"
import { ReferenceLink } from "@/plugins/extensions/reference/referenceLink"
import { Actions, Getters } from "@/store"
import validators from "@/utils/validators"

const components = {
  CustomLabel,
  ToDoContent,
  ToDoSelectUser,
  TodoWidgetDatePicker,
  Editor,
  Field,
  ErrorMessage
}

/** Renders the design-aligned ToDo creation form while preserving the existing save contract. */
@Options({
  name: "ToDoAdd",
  components
})
export default class ToDoAdd extends mixins(ToDoCreateAndUpdate) {
  @Prop({ default: "" }) className: string
  @Getter(Getters.GET_USER_CURRENT_PROFILE) readonly currentUser: User
  @Action(Actions.ATTACHMENT_UPLOAD) attachmentUpload: (
    payload: UploadAttachment
  ) => Promise<FileMetaData[]>
  @Action(Actions.DELETE_TODO) dismissToDo: (todoItemId: string) => Promise<boolean>

  validators = validators
  addRules = false
  isDismissConfirmationVisible = false

  recipientScopes: ToDoCompletionRule[] = ["ANYONE", "EVERYONE"]

  get toDoName() {
    return this.toDo.name || ""
  }

  get canDismissToDo() {
    return (
      Boolean(this.toDo.id) &&
      this.toDo.status !== "COMPLETED" &&
      this.toDo.createdBy?.id === this.currentUser.id
    )
  }

  /** Keeps existing file references visible while a ToDo description is edited. */
  get extensions() {
    const defaultExtensions = [
      AttachmentFile(this.attachmentUpload, this.attachmentTarget, true),
      ReferenceAt,
      ReferenceAllUser,
      ReferenceHashProject,
      ReferenceHashFile,
      ReferenceHashTip,
      ReferenceLink
    ]
    if (!this.currentProjectId) {
      return defaultExtensions
    }
    return [
      ...defaultExtensions,
      ReferenceHashMilestone,
      ReferenceHashEpic,
      ReferenceHashIdea,
      ReferenceHashTeam,
      ReferenceHashSprint,
      ReferenceHashItem,
      ReferenceHashTask
    ]
  }

  get attachmentTarget(): TypeConfig | null {
    if (!this.toDo.id) return null

    return {
      type: "TODO_ITEM",
      targetId: this.toDo.id
    }
  }

  onCheckValid(validate, reset) {
    this.addRules = true
    this.$nextTick(() => {
      this.onValidateAndSave(validate, reset)
    })
  }

  /** Deletes the ToDo after inline confirmation and returns to the active list. */
  async onDismissToDo() {
    if (!(await this.dismissToDo(this.toDo.id))) return
    this.$emit("onCancel", true)
  }

  async onValidateAndSave(validate, reset) {
    const { valid } = await validate()
    if (!valid) {
      return
    }
    this.addRules = false
    const isSaved = await this.onSave()
    if (!isSaved) {
      return
    }
    reset()
  }
}
</script>
